/**
 * 다국어 문자열 사전 (ko / en / ja / zh). 접속자 브라우저 언어로 자동 선택(i18n.ts).
 * 대상: 개발자 전용이 아니라 사무직 전반. 개발 용어(빌드/커밋/리팩터 등) 배제.
 * 문자열의 {name} 자리표시자는 t(key, params)에서 치환된다.
 */
export type Locale = 'ko' | 'en' | 'ja' | 'zh';

export const STRINGS: Record<Locale, Record<string, string>> = {
  ko: {
    pageTitle: 'Desk Smash — 사무실 책상 뿌시기',
    tabTitle: '사무실 책상 뿌시기',
    metaDesc: '왜 화가 나셨나요? 책상 위 물건을 잡아 던지고 내리쳐 전부 뿌셔버리는 3D 스트레스 해소 게임 💥',
    title: '사무실 책상 뿌시기',
    startBtn: '뿌셔! →',
    howto: '책상 위 물건을 잡아 던지거나 내리쳐 전부 부수세요.',
    best: '최고 점수 {score} · 최고 콤보 {combo}',
    itemsLeft: '남은 물건 {n}',
    combo: '{n} 콤보 ×{m}',
    ttReset: '리셋',
    ttSound: '음향 크기',
    sweepBtn: '쓸어버리기',
    flipBtn: '책상 뒤집기',
    sweepHint: '책상을 누른 채 반대쪽으로 휙 드래그!',
    flipHint: '책상 앞쪽을 잡고 위로 들어올리세요!',
    sfxCredit: '효과음',
    newBest: '🏆 신기록!',
    totalScore: 'TOTAL SCORE',
    maxCombo: 'MAX {n} COMBO',
    newBestBadge: '🏆 NEW BEST',
    shareBtn: '공유하기 🔗',
    replayBtn: '다시하기',
    linkCopied: '링크를 복사했어요!',
    shareText: 'Desk Smash에서 {score}점 💥 (최고 {combo} 콤보) — 너도 책상 부수러 와!',
    initError: '초기화에 실패했어요. WebGL2/WebAssembly를 지원하는 최신 브라우저에서 다시 시도해 주세요.',
    initErrorWasm: '이 게임은 WebAssembly가 필요한데 브라우저에서 비활성화되어 있어요. 보안·차단 모드나 확장 프로그램이 막고 있을 수 있어요 — 해당 설정을 해제하거나 다른 브라우저에서 다시 시도해 주세요.',
  },
  en: {
    pageTitle: 'Desk Smash — Office Desk Smasher',
    tabTitle: 'Desk Smash',
    metaDesc: 'What made you angry today? Grab, throw and smash everything on the desk in this 3D stress-relief game 💥',
    title: 'DESK SMASH',
    startBtn: 'SMASH! →',
    howto: 'Grab, throw, and smash everything on the desk.',
    best: 'Best {score} · Max combo {combo}',
    itemsLeft: 'Items left {n}',
    combo: '{n} COMBO ×{m}',
    ttReset: 'Reset',
    ttSound: 'Volume',
    sweepBtn: 'Sweep it all',
    flipBtn: 'Flip the desk',
    sweepHint: 'Press the desk and drag across!',
    flipHint: 'Grab the front of the desk and drag up!',
    sfxCredit: 'SFX',
    newBest: '🏆 New Best!',
    totalScore: 'TOTAL SCORE',
    maxCombo: 'MAX {n} COMBO',
    newBestBadge: '🏆 NEW BEST',
    shareBtn: 'Share 🔗',
    replayBtn: 'Play again',
    linkCopied: 'Link copied!',
    shareText: 'I smashed {score} points on Desk Smash 💥 (max {combo} combo) — come wreck a desk too!',
    initError: 'Failed to start. Please try again in a modern browser with WebGL2/WebAssembly support.',
    initErrorWasm: 'This game needs WebAssembly, but it is disabled in your browser. A security/lockdown mode or an extension may be blocking it — turn that setting off, or try a different browser.',
  },
  ja: {
    pageTitle: 'Desk Smash — オフィス机ぶっ壊し',
    tabTitle: 'オフィス机ぶっ壊し',
    metaDesc: '今日は何にイライラした？机の上の物をつかんで投げて全部ぶっ壊す3Dストレス解消ゲーム 💥',
    title: 'オフィス机ぶっ壊し',
    startBtn: 'ぶっ壊す！ →',
    howto: '机の上の物をつかんで、投げたり叩いたりして全部壊そう。',
    best: 'ベスト {score} · 最大コンボ {combo}',
    itemsLeft: '残り {n} 個',
    combo: '{n} コンボ ×{m}',
    ttReset: 'リセット',
    ttSound: '音量',
    sweepBtn: 'なぎ払う',
    flipBtn: 'ちゃぶ台返し',
    sweepHint: '机を押しながら反対側へドラッグ！',
    flipHint: '机の前側をつかんで上へ持ち上げろ！',
    sfxCredit: '効果音',
    newBest: '🏆 新記録！',
    totalScore: 'TOTAL SCORE',
    maxCombo: 'MAX {n} COMBO',
    newBestBadge: '🏆 NEW BEST',
    shareBtn: 'シェア 🔗',
    replayBtn: 'もう一度',
    linkCopied: 'リンクをコピーしました！',
    shareText: 'Desk Smashで{score}点 💥（最大{combo}コンボ）— 君も机を壊しに来い！',
    initError: '起動に失敗しました。WebGL2/WebAssembly対応の最新ブラウザで再試行してください。',
    initErrorWasm: 'このゲームには WebAssembly が必要ですが、お使いのブラウザで無効になっています。セキュリティ／ロックダウンモードや拡張機能がブロックしている可能性があります — その設定を解除するか、別のブラウザでお試しください。',
  },
  zh: {
    pageTitle: 'Desk Smash — 办公室砸桌子',
    tabTitle: '办公室砸桌子',
    metaDesc: '今天为什么生气了？抓起桌上的东西扔出去、砸个粉碎的3D解压游戏 💥',
    title: '办公室砸桌子',
    startBtn: '砸！ →',
    howto: '抓起桌上的东西，扔或砸，把它们全部砸碎。',
    best: '最高 {score} · 最大连击 {combo}',
    itemsLeft: '剩余 {n} 个',
    combo: '{n} 连击 ×{m}',
    ttReset: '重置',
    ttSound: '音量',
    sweepBtn: '横扫一切',
    flipBtn: '掀桌子',
    sweepHint: '按住桌面并向另一侧拖动！',
    flipHint: '抓住桌子前沿向上拖动！',
    sfxCredit: '音效',
    newBest: '🏆 新纪录！',
    totalScore: 'TOTAL SCORE',
    maxCombo: 'MAX {n} COMBO',
    newBestBadge: '🏆 NEW BEST',
    shareBtn: '分享 🔗',
    replayBtn: '再玩一次',
    linkCopied: '已复制链接！',
    shareText: '我在 Desk Smash 砸了 {score} 分 💥（最高 {combo} 连击）— 快来一起砸桌子！',
    initError: '启动失败。请在支持 WebGL2/WebAssembly 的现代浏览器中重试。',
    initErrorWasm: '本游戏需要 WebAssembly，但你的浏览器已将其禁用。安全／锁定模式或扩展程序可能正在阻止它 — 请关闭该设置，或改用其他浏览器重试。',
  },
};

/** 성적 드립 문구(사무직 전반 톤). 점수 구간 + 콤보 보너스. */
export interface QuipData {
  tiers: { minScore: number; lines: string[] }[];
  comboBonus: { minCombo: number; line: string }[];
}

export const QUIPS: Record<Locale, QuipData> = {
  ko: {
    tiers: [
      { minScore: 0, lines: ['오늘은 스트레스가 이겼네요.', '일단 부숴는 봤습니다.', '화는 조금 남았지만 책상은 무사합니다.'] },
      { minScore: 1500, lines: ['퇴근이 가까워지는 파괴력.', '이 정도면 좀 시원하네요.', '회의 세 개는 날린 셈 칩시다.'] },
      { minScore: 4000, lines: ['부장님도 놀랄 파괴력.', '책상이 남아나질 않네요.', '월요일의 분노를 담았군요.'] },
      { minScore: 8000, lines: ['전설의 사무실 파괴왕.', '이건 총무팀도 못 막습니다.', '분노 만렙, 책상은 0렙.'] },
    ],
    comboBonus: [
      { minCombo: 8, line: ' (콤보 마스터!)' },
      { minCombo: 5, line: ' (콤보 좀 치시네요.)' },
    ],
  },
  en: {
    tiers: [
      { minScore: 0, lines: ['Stress won this round.', 'Well, you smashed something.', 'Still a little angry, but the desk survived.'] },
      { minScore: 1500, lines: ['Almost-clocking-out level of destruction.', 'Now that felt good.', "Let's call it three meetings cancelled."] },
      { minScore: 4000, lines: ['Even your boss would be impressed.', 'Nothing left on that desk.', 'That was pure Monday rage.'] },
      { minScore: 8000, lines: ['Legendary office wrecker.', 'Not even facilities can stop this.', 'Max rage, zero desk.'] },
    ],
    comboBonus: [
      { minCombo: 8, line: ' (Combo master!)' },
      { minCombo: 5, line: ' (Nice combos.)' },
    ],
  },
  ja: {
    tiers: [
      { minScore: 0, lines: ['今日はストレスの勝ち。', 'とりあえず壊した。', 'まだ少しイライラ、でも机は無事。'] },
      { minScore: 1500, lines: ['退勤が近づく破壊力。', 'なかなかスッキリ。', '会議3つ分は消えたことにしよう。'] },
      { minScore: 4000, lines: ['部長も驚く破壊力。', '机が残らない。', '月曜の怒りが乗ってる。'] },
      { minScore: 8000, lines: ['伝説のオフィス破壊王。', '総務でも止められない。', '怒りMAX、机はゼロ。'] },
    ],
    comboBonus: [
      { minCombo: 8, line: '（コンボマスター！）' },
      { minCombo: 5, line: '（いいコンボ。）' },
    ],
  },
  zh: {
    tiers: [
      { minScore: 0, lines: ['今天压力赢了。', '好歹砸了点东西。', '还有点气，但桌子没事。'] },
      { minScore: 1500, lines: ['接近下班的破坏力。', '这下爽了。', '就当取消了三个会。'] },
      { minScore: 4000, lines: ['连老板都会惊讶。', '桌上啥都不剩了。', '这是纯粹的周一怒火。'] },
      { minScore: 8000, lines: ['传奇办公室破坏王。', '连后勤都拦不住。', '怒气满级，桌子零级。'] },
    ],
    comboBonus: [
      { minCombo: 8, line: '（连击大师！）' },
      { minCombo: 5, line: '（连击不错。）' },
    ],
  },
};
